<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">
                                                {{ model?.name || 'Loading...' }}
                                        </h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">3D Model ID: {{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading 3D model...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="p-6">
                                        <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">3D Model Properties</h3>
                                        <div class="max-w-lg mb-8">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                <div class="flex gap-2">
                                                        <input
                                                                v-model="model.name"
                                                                type="text"
                                                                placeholder="Enter 3D model name"
                                                                class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                        <button
                                                                type="button"
                                                                :disabled="isSaving"
                                                                class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
                                                                @click="saveModel"
                                                        >
                                                                {{ isSaving ? 'Saving...' : 'Save' }}
                                                        </button>
                                                </div>
                                        </div>

                                        <!-- Scene File Section -->
                                        <div class="mb-6 pt-6 border-t border-gray-200">
                                                <h3 class="text-sm font-semibold text-gray-900 mb-4">Scene File (GLB)</h3>
                                                <div class="max-w-lg">
                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Scene URL</label>
                                                        <div class="flex gap-2">
                                                                <input
                                                                        v-model="model.sceneUrl"
                                                                        type="text"
                                                                        placeholder="https://..."
                                                                        class="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                                <button
                                                                        type="button"
                                                                        class="py-2 px-4 text-sm font-medium text-orange-7 bg-transparent border border-orange-4 rounded-lg cursor-pointer transition-colors hover:bg-orange-1"
                                                                        @click="uploadFile('glb')"
                                                                >
                                                                        Upload
                                                                </button>
                                                        </div>
                                                        <p v-if="model.sceneUrl" class="text-xs text-gray-500 mt-1 truncate">{{ model.sceneUrl }}</p>
                                                </div>
                                        </div>

                                        <!-- Environment Section -->
                                        <div class="mb-6 pt-6 border-t border-gray-200">
                                                <h3 class="text-sm font-semibold text-gray-900 mb-4">Environment</h3>
                                                <div class="grid gap-4 max-w-lg">
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">HDR Texture URL</label>
                                                                <div class="flex gap-2">
                                                                        <input
                                                                                v-model="model.envTextureUrl"
                                                                                type="text"
                                                                                placeholder="https://..."
                                                                                class="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                        <button
                                                                                type="button"
                                                                                class="py-2 px-4 text-sm font-medium text-orange-7 bg-transparent border border-orange-4 rounded-lg cursor-pointer transition-colors hover:bg-orange-1"
                                                                                @click="uploadFile('hdr')"
                                                                        >
                                                                                Upload
                                                                        </button>
                                                                </div>
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Environment Intensity</label>
                                                                <input
                                                                        v-model.number="model.envIntensity"
                                                                        type="number"
                                                                        step="0.1"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Texture Section -->
                                        <div class="mb-6 pt-6 border-t border-gray-200">
                                                <h3 class="text-sm font-semibold text-gray-900 mb-4">Texture</h3>
                                                <div class="grid gap-4 max-w-lg">
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Texture URL</label>
                                                                <div class="flex gap-2">
                                                                        <input
                                                                                v-model="model.textureUrl"
                                                                                type="text"
                                                                                placeholder="https://..."
                                                                                class="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                        <button
                                                                                type="button"
                                                                                class="py-2 px-4 text-sm font-medium text-orange-7 bg-transparent border border-orange-4 rounded-lg cursor-pointer transition-colors hover:bg-orange-1"
                                                                                @click="uploadFile('texture')"
                                                                        >
                                                                                Upload
                                                                        </button>
                                                                </div>
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Texture Material Name</label>
                                                                <div class="flex gap-2 items-center">
                                                                        <input
                                                                                v-model="model.textureMaterialName"
                                                                                type="text"
                                                                                :disabled="model.textureMaterialName === null"
                                                                                class="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                                :class="model.textureMaterialName === null ? 'bg-gray-100' : 'bg-white'"
                                                                        >
                                                                        <label class="flex items-center gap-1 text-xs text-gray-500 cursor-pointer whitespace-nowrap">
                                                                                <input
                                                                                        type="checkbox"
                                                                                        :checked="model.textureMaterialName === null"
                                                                                        class="cursor-pointer"
                                                                                        @change="model.textureMaterialName = ($event.target as HTMLInputElement).checked ? null : ''"
                                                                                >
                                                                                Null
                                                                        </label>
                                                                </div>
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Print Material Name</label>
                                                                <div class="flex gap-2 items-center">
                                                                        <input
                                                                                v-model="model.printMaterialName"
                                                                                type="text"
                                                                                :disabled="model.printMaterialName === null"
                                                                                class="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                                :class="model.printMaterialName === null ? 'bg-gray-100' : 'bg-white'"
                                                                        >
                                                                        <label class="flex items-center gap-1 text-xs text-gray-500 cursor-pointer whitespace-nowrap">
                                                                                <input
                                                                                        type="checkbox"
                                                                                        :checked="model.printMaterialName === null"
                                                                                        class="cursor-pointer"
                                                                                        @change="model.printMaterialName = ($event.target as HTMLInputElement).checked ? null : ''"
                                                                                >
                                                                                Null
                                                                        </label>
                                                                </div>
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Transparency Mode</label>
                                                                <div class="flex gap-2 items-center">
                                                                        <input
                                                                                v-model.number="model.transparencyMode"
                                                                                type="number"
                                                                                :disabled="model.transparencyMode === null"
                                                                                class="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                                :class="model.transparencyMode === null ? 'bg-gray-100' : 'bg-white'"
                                                                        >
                                                                        <label class="flex items-center gap-1 text-xs text-gray-500 cursor-pointer whitespace-nowrap">
                                                                                <input
                                                                                        type="checkbox"
                                                                                        :checked="model.transparencyMode === null"
                                                                                        class="cursor-pointer"
                                                                                        @change="model.transparencyMode = ($event.target as HTMLInputElement).checked ? null : 0"
                                                                                >
                                                                                Null
                                                                        </label>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Camera Section -->
                                        <div class="mb-6 pt-6 border-t border-gray-200">
                                                <h3 class="text-sm font-semibold text-gray-900 mb-4">Camera Settings</h3>
                                                <div class="grid grid-cols-2 gap-4 max-w-lg">
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Camera Alpha</label>
                                                                <input
                                                                        v-model.number="model.cameraAlpha"
                                                                        type="number"
                                                                        step="0.01"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Camera Beta</label>
                                                                <input
                                                                        v-model.number="model.cameraBeta"
                                                                        type="number"
                                                                        step="0.01"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Camera Radius</label>
                                                                <input
                                                                        v-model.number="model.cameraRadius"
                                                                        type="number"
                                                                        step="0.1"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Lower Radius Limit</label>
                                                                <input
                                                                        v-model.number="model.lowerRadiusLimit"
                                                                        type="number"
                                                                        step="0.1"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                        <div>
                                                                <label class="block text-xs font-medium text-gray-500 mb-1">Upper Radius Limit</label>
                                                                <input
                                                                        v-model.number="model.upperRadiusLimit"
                                                                        type="number"
                                                                        step="0.1"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                >
                                                        </div>
                                                </div>
                                        </div>

                                </div>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div
                        v-if="toastMessage"
                        class="toast-container fixed right-6 z-50 transition-all duration-300"
                        :class="toastMessage ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'"
                >
                        <div
                                class="flex items-center gap-3 py-3 px-4 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'"
                        >
                                <svg
                                        v-if="toastVariant === 'success'"
                                        class="w-5 h-5 text-green-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2"
                                >
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg
                                        v-else
                                        class="w-5 h-5 text-red-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2"
                                >
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span
                                        class="text-sm font-medium"
                                        :class="toastVariant === 'success' ? 'text-green-800' : 'text-red-800'"
                                >
                                        {{ toastMessage }}
                                </span>
                                <button
                                        type="button"
                                        class="ml-2 p-1 rounded transition-colors"
                                        :class="toastVariant === 'success' ? 'text-green-500 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { PickerOptions, PickerResponse } from 'filestack-js';
import Model3DService from 'services/Model3DService';

export default defineComponent({
        name: 'EditModel3DPage',
        setup() {
                const router = useRouter();
                const route = useRoute();
                const instance = getCurrentInstance();

                const routeId = computed(() => route.params.id as string);
                const isLoading = ref(true);
                const isSaving = ref(false);
                const model = ref<any>({});
                const toastMessage = ref('');
                const toastVariant = ref<'success' | 'danger'>('success');

                const goBack = () => {
                        router.push('/model3d');
                };

                const loadModel = async () => {
                        isLoading.value = true;
                        try {
                                const data = await Model3DService.get(routeId.value);
                                model.value = data;
                        } catch (error) {
                                console.error('Failed to load model:', error);
                                toastMessage.value = 'Failed to load 3D model';
                                toastVariant.value = 'danger';
                        } finally {
                                isLoading.value = false;
                        }
                };

                const saveModel = async () => {
                        isSaving.value = true;
                        try {
                                await Model3DService.update(routeId.value, model.value);
                                toastMessage.value = '3D model saved successfully';
                                toastVariant.value = 'success';
                                setTimeout(() => {
                                        toastMessage.value = '';
                                }, 3000);
                        } catch (error) {
                                console.error('Failed to save model:', error);
                                toastMessage.value = 'Failed to save 3D model';
                                toastVariant.value = 'danger';
                        } finally {
                                isSaving.value = false;
                        }
                };

                const uploadFile = (type: string) => {
                        const filestackClient = instance?.appContext.config.globalProperties.$filestack;
                        if (!filestackClient) return;

                        const acceptMap: Record<string, string[]> = {
                                glb: ['.glb'],
                                hdr: ['.hdr'],
                                texture: ['image/*'],
                        };

                        const options: PickerOptions = {
                                accept: acceptMap[type] || ['*/*'],
                                maxFiles: 1,
                                onUploadDone: (result: PickerResponse) => {
                                        if (result.filesUploaded.length > 0) {
                                                const url = result.filesUploaded[0].url;
                                                if (type === 'glb') {
                                                        model.value.sceneUrl = url;
                                                } else if (type === 'hdr') {
                                                        model.value.envTextureUrl = url;
                                                } else if (type === 'texture') {
                                                        model.value.textureUrl = url;
                                                }
                                        }
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const hideToast = () => {
                        toastMessage.value = '';
                };

                onMounted(() => {
                        loadModel();
                });

                return {
                        routeId,
                        isLoading,
                        isSaving,
                        model,
                        toastMessage,
                        toastVariant,
                        saveModel,
                        uploadFile,
                        goBack,
                        hideToast,
                };
        },
});
</script>

<style>
.toast-container {
        bottom: 1.5rem !important;
}
</style>
